import type { ReactNode } from 'react';
import type { ClientDesignation, ClientRow } from '../../api/types.js';
import { color, font, fontSize, layout } from '../../theme/tokens.js';
import { DataTable, type Column } from '../DataTable/DataTable.js';
import { MetricCell, money } from '../MetricCell/MetricCell.js';
import { TRENDED_TRACK_PX } from '../RegionTable/RegionTable.js';

/**
 * The by-client breakdown (FR-038, FR-039, FR-040, PRD §6.3).
 *
 * Rows are the region's **target accounts**, and the targeting control selects how much of that
 * list is shown: `Target` = Primary, `All` = Primary + Secondary (FR-T11, restoring
 * `blueprint codebase/BoB Manager.dc.html` `buildClientRows` line 1658). A Primary account is
 * emphasised and a Secondary one plain, which is the blueprint's own treatment (line 1735) —
 * replacing the grey `COTLR` tag, a mark the design never carried.
 *
 * Superseded comment, kept for one release so a reader tracing the change can find it:
 * "Rows are **target ∪ COTLR** — the region's target accounts plus clients with jobs in the
 * region that are not on the target list. All sixteen PRD §6.3 columns, on the shared
 * `<DataTable>` so `fullLabel`, hover parity, and the `—` fallback are the same rules the
 * Overview table follows.
 *
 * Selecting a row highlights it and scopes the chart to that client; a clear control restores
 * region scope (FR-040).
 */

/**
 * The action column's width — the same 40px `<RegionTable>` spends, for the same reasons.
 *
 * `layout.breakdownMinWidth` was widened by exactly this when the column landed, so the sixteen
 * metric tracks keep the room they had; see that token's note.
 */
const ACTION_COLUMN_WIDTH = 68;

/**
 * The columns that draw a trend — the arrow and its 30-day percentage, `▲12%` (2026-09-27). These
 * are exactly the metrics `clients.ts` runs `periodTrend`/`stockTrend` over; every other column is
 * stored without one.
 *
 * ⚠ Only these reserve `<MetricCell>`'s 36px trend slot. It was an 8px arrow slot until
 * 2026-09-27, reserved on every column by default; at 36px that default would be ~430px of empty
 * reservation across eleven columns that never trend.
 */
const TRENDED_KEYS: ReadonlySet<string> = new Set(['toa', 'jobs', 'subs', 'offers', 'starts']);

/**
 * What a `1fr` metric track gets at `layout.breakdownMinWidth`: `(1328 − 68) / (1.5 + 1.3 + 16)`.
 */
const DEFAULT_TRACK_PX = 67;

/**
 * The table's floor once the trended columns carry their trend — `layout.breakdownMinWidth` plus
 * what those five floors take over the default. **Widened rather than absorbed**, for the reason
 * that token's own note gives: a floor raised without moving the minimum takes its width off the
 * sibling tracks, which is how `#JOBS` came to be cut on Region Performance.
 */
const TABLE_MIN_WIDTH = `${
  Number.parseInt(layout.breakdownMinWidth, 10) +
  TRENDED_KEYS.size * (TRENDED_TRACK_PX - DEFAULT_TRACK_PX)
}px`;

export interface ClientBreakdownProps {
  rows: ClientRow[];
  selectedClientKey: string | null;
  onSelectClient: (clientKey: string) => void;
  /** The card's own header bar — title and period/targeting controls (blueprint § breakdown). */
  header?: ReactNode;
  /**
   * Renders the per-row export control, when the page supplies one (FR-091).
   *
   * ⚠ **A render prop, not an `(clientKey) => void` callback** — the same shape and the same
   * reason as `<RegionTable>`'s `onExportRegion`. The control is a `<Disclosure>` that owns
   * streaming progress, a consent round trip and a completion dialog, and that state has to
   * survive the table re-sorting around it rather than living in the table.
   *
   * ⚠ It takes the **row**, not the key: the export needs the key to ask the server with and the
   * name to label itself with, and the two are different strings (see `ExportButtonProps.client`).
   */
  onExportClient?: (row: ClientRow) => ReactNode;
}

/*
 * `money` is the shared one, so Margin here compacts on the same threshold as GM on Delivery and
 * as every count in the product. (The column is labelled `M` / `Avg M` here and `GM` on Delivery;
 * the metric keys stay `gm` / `avgGm`, because a key is storage and a label is not.) A currency column that stayed long while the counts beside it
 * shortened would read as the odd one out rather than as a deliberate unit.
 */

const percent = (value: number) => `${value}%`;

/**
 * The client name's tooltip. A lookup rather than a Primary/else-Secondary branch: the `else` is
 * how a 3rd-tier account would have been labelled Secondary (owner ruling 2026-10-29).
 */
const DESIGNATION_LABEL: Record<ClientDesignation, string> = {
  primary: 'Primary target account',
  secondary: 'Secondary target account',
  third: '3rd target account',
};

export function ClientBreakdown({
  rows,
  selectedClientKey,
  onSelectClient,
  header,
  onExportClient,
}: ClientBreakdownProps) {
  const metricColumn = (
    key: string,
    label: string,
    fullLabel: string,
    options: {
      format?: (v: number) => string;
      bold?: boolean;
      colorOverride?: string;
      /**
       * The lineage key, where it is not the storage key.
       *
       * ⚠ Only Margin and Avg Margin pass it, and only because `gm` / `avgGm` still describe
       * **Delivery's** GM — the `ch_gp_data` SKU join — while these two are
       * `ch_run_rate.NET_MARGIN` over active TOA (owner decision 2026-09-14). One key cannot
       * carry both sentences, and `provenanceFor` renders no footer rather than a guess, so a
       * shared key would print a true sentence under the wrong number. It collapses away the
       * day Delivery's column follows.
       */
      provenanceKey?: string;
    } = {},
  ): Column<ClientRow> => ({
    key,
    label,
    fullLabel,
    align: 'right',
    ...(TRENDED_KEYS.has(key) ? { width: `minmax(${TRENDED_TRACK_PX}px, 1fr)` } : {}),
    render: (row) => (
      <MetricCell
        metric={row.metrics[key]}
        label={fullLabel}
        metricKey={options.provenanceKey ?? key}
        format={options.format}
        bold={options.bold}
        colorOverride={options.colorOverride}
        showTrendArrow={TRENDED_KEYS.has(key)}
      />
    ),
    sortValue: (row) => row.metrics[key]?.value ?? null,
  });

  const columns: Array<Column<ClientRow>> = [
    {
      key: 'client',
      label: 'Client',
      fullLabel: 'Client / target account name',
      width: '1.5fr',
      align: 'left',
      render: (row) => (
        <div
          style={{
            padding: '9px 14px',
            fontSize: fontSize.bodyStrong,
            fontWeight: row.designation === 'primary' ? 600 : 500,
            color: row.designation === 'primary' ? color.ink : color.inkRow,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={`${row.clientName} — ${DESIGNATION_LABEL[row.designation]}`}
        >
          {row.clientName}
        </div>
      ),
      sortValue: (row) => row.clientName,
    },
    {
      key: 'msp',
      label: 'MSP',
      fullLabel: 'Managed Service Provider handling this client',
      width: '1.3fr',
      align: 'left',
      render: (row) => (
        <div
          className="mono"
          style={{
            padding: '9px 8px',
            fontFamily: font.mono,
            fontSize: fontSize.captionStrong,
            color: color.inkMuted,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {row.msp ?? '—'}
        </div>
      ),
      sortValue: (row) => row.msp,
    },
    metricColumn('toa', 'TOA', 'Active Travelers on Assignment', { bold: true }),
    metricColumn('jobs', 'Jobs', 'Job openings, total'),
    metricColumn('jobsNursing', 'Jobs-N', 'Job openings — Nursing', {
      colorOverride: color.nursing,
    }),
    metricColumn('jobsAllied', 'Jobs-A', 'Job openings — Allied', {
      colorOverride: color.allied,
    }),
    metricColumn('subs', 'Subs', 'Submissions'),
    metricColumn('offers', 'Offers', 'Offers extended'),
    metricColumn('starts', 'Starts', 'Assignments started'),
    metricColumn('terms', 'Terms', 'Assignments terminated'),
    metricColumn('backouts', 'BO', 'Backouts'),
    // Capped at openings per job, so it cannot exceed 100% from over-submission (PRD §9.3).
    metricColumn('covPct', 'Cov %', 'Coverage % of open jobs staffed', { format: percent }),
    metricColumn('convPct', 'Conv %', 'Submission-to-offer conversion rate', {
      format: percent,
      colorOverride: color.nursing,
    }),
    metricColumn('gm', 'M', 'Margin, total', { format: money, provenanceKey: 'netMargin' }),
    metricColumn('avgGm', 'Avg M', 'Average margin per TOA', {
      format: money,
      provenanceKey: 'avgNetMargin',
    }),
    metricColumn('gp', 'GP', 'Gross profit', { format: money, bold: true }),
    ...(onExportClient
      ? [
          {
            key: 'actions',
            label: 'Action',
            fullLabel: 'Action — export this account to a Google Sheet',
            align: 'center' as const,
            width: `${ACTION_COLUMN_WIDTH}px`,
            sortable: false,
            /*
             * ⚠ **The row is itself a button, so anything interactive inside it has to stop the
             * event escaping.** `<DataTable>` puts `onClick` and an Enter/Space `onKeyDown` on
             * the row to select the client and rescope the chart — so without this, pressing
             * Export would also rescope the page under the reader.
             *
             * ⚠ **The wrapper goes around the whole control, not its trigger.** The completion
             * dialog is in this subtree too: `position: fixed` moves where a node paints, never
             * where its events go, so "Open sheet", "Close" and the scrim all bubble here as
             * well — and a click on the link would otherwise rescope the chart on the way out.
             *
             * ⚠ **Only Enter and Space are stopped, never every key.** React's
             * `stopPropagation` calls the native one, so blanket-stopping `keydown` would keep
             * Escape from reaching the `window` listener that closes that dialog. Those two keys
             * are exactly what the row listens for.
             */
            render: (row: ClientRow) => (
              <div
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                }}
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                {onExportClient(row)}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <DataTable<ClientRow>
      caption="By client"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.clientKey}
      onRowClick={(row) => onSelectClient(row.clientKey)}
      isRowSelected={(row) => row.clientKey === selectedClientKey}
      minWidth={TABLE_MIN_WIDTH}
      emptyMessage="No target accounts for this region and period."
      header={header}
    />
  );
}
